import api from "./axios";

/**
 * All upload endpoints below send files to the API, which stores them on **Backblaze B2**
 * (see `mintly-back/src/utils/backblaze.js`). Responses include `{ success, url, fileId }`.
 */

/**
 * Upload profile avatar
 * @param {File} file - Image file
 * @returns {Promise} Upload response with URL
 */
export const uploadAvatar = async (file) => {
  const formData = new FormData();
  formData.append("avatar", file);
  
  const response = await api.post("/upload/profile/avatar", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Upload profile banner
 * @param {File} file - Image file
 * @returns {Promise} Upload response with URL
 */
export const uploadBanner = async (file) => {
  const formData = new FormData();
  formData.append("banner", file);
  
  const response = await api.post("/upload/profile/banner", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Upload mint space logo
 * @param {File} file - Image file
 * @returns {Promise} Upload response with URL
 */
export const uploadMintSpaceLogo = async (file) => {
  const formData = new FormData();
  formData.append("logo", file);
  
  const response = await api.post("/upload/mint-space/logo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Upload mint space welcome banner
 * @param {File} file - Image file
 * @returns {Promise} Upload response with URL
 */
export const uploadWelcomeBanner = async (file) => {
  const formData = new FormData();
  formData.append("banner", file);
  
  const response = await api.post("/upload/mint-space/welcome-banner", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Upload campaign logo
 * @param {File} file - Image file
 * @returns {Promise} Upload response with URL
 */
export const uploadCampaignLogo = async (file) => {
  const formData = new FormData();
  formData.append("logo", file);
  
  const response = await api.post("/upload/campaign/logo", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/** Upload one campaign attachment (Backblaze bucket `mintly-files`). */
export const uploadCampaignAttachmentFile = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post("/upload/campaign/attachments", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Upload a Mint Space policy document (Terms of Service or Privacy Policy).
 * `kind` narrows the storage subfolder — the actual policy field it belongs to is set
 * by the caller when patching the mint space.
 */
export const uploadMintSpacePolicyFile = async (file, kind = "terms") => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("kind", kind);
  const response = await api.post("/upload/mint-space/policy", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

/**
 * Generic image upload
 * @param {File} file - Image file
 * @param {string} folder - Optional folder name
 * @returns {Promise} Upload response with URL
 */
export const uploadImage = async (file, folder = "general") => {
  const formData = new FormData();
  formData.append("image", file);
  if (folder) {
    formData.append("folder", folder);
  }
  
  const response = await api.post("/upload/image", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Video upload
 * @param {File} file - Video file
 * @param {string} folder - Optional folder name
 * @returns {Promise} Upload response with URL
 */
export const uploadVideo = async (file, folder = "announcements") => {
  const formData = new FormData();
  formData.append("video", file);
  if (folder) {
    formData.append("folder", folder);
  }
  
  const response = await api.post("/upload/video", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

